import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, apiError } from '../lib/api';

// Handles both /payment/success and /payment/cancel via `mode` prop.
export default function PaymentResult({ mode }) {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState(mode === 'success' ? 'polling' : 'cancelled');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const tries = useRef(0);

  useEffect(() => {
    if (mode !== 'success' || !sessionId) return;
    let cancelled = false;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (['failed','expired','refunded'].includes(data.payment_status)) { setStatus(data.payment_status); return; }
        if (tries.current >= 20) { setStatus('timeout'); return; } // ~40s max
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setErr(apiError(e, 'Could not verify payment')); setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [mode, sessionId]);

  const back = <div className="mt-4"><Link to="/owner/boost" className="btn-ap-outline">Back to plans</Link></div>;

  return (
    <div className="container my-5" style={{ maxWidth: 560 }} data-testid={`payment-${mode}`}>
      <div className="ap-card text-center">
        {mode === 'cancel' && (
          <>
            <div style={{ fontSize: '3rem' }}>😕</div>
            <h1 className="mt-2">Payment cancelled</h1>
            <p className="text-muted-2">No charge was made. Come back anytime.</p>
            {back}
          </>
        )}
        {mode === 'success' && status === 'polling' && (
          <>
            <span className="ap-loader" style={{ width: 32, height: 32 }} />
            <h2 className="mt-3">Confirming your payment…</h2>
            <p className="text-muted-2 small">Please don't close this page.</p>
          </>
        )}
        {mode === 'success' && status === 'paid' && (
          <>
            <div style={{ fontSize: '3rem' }}>🎉</div>
            <h1 className="mt-2">Payment successful!</h1>
            <p className="text-muted-2">Your business has been upgraded to <strong>{data?.plan_key}</strong>. It will appear at the top of listings shortly.</p>
            <p className="small text-muted-2">Amount charged: ₹ {data?.amount} · Session: <code style={{ fontSize: '.75rem' }}>{sessionId}</code></p>
            <div className="mt-4 d-flex justify-content-center gap-2">
              <Link to="/owner/businesses" className="btn-ap" data-testid="pay-goto-businesses">Go to my businesses</Link>
              <Link to="/owner/payments" className="btn-ap-outline" data-testid="pay-goto-history">Payment history</Link>
            </div>
          </>
        )}
        {mode === 'success' && ['failed','expired','refunded'].includes(status) && (
          <>
            <div style={{ fontSize: '3rem' }}>⚠️</div>
            <h1 className="mt-2">Payment {status}</h1>
            <p className="text-muted-2">We couldn't complete the payment. You have not been charged.</p>
            {back}
          </>
        )}
        {mode === 'success' && status === 'timeout' && (
          <>
            <div style={{ fontSize: '3rem' }}>⏳</div>
            <h1 className="mt-2">Still confirming…</h1>
            <p className="text-muted-2">Your bank is taking longer than usual. Check the payment history in a minute.</p>
            <div className="mt-4"><Link to="/owner/payments" className="btn-ap">View payment history</Link></div>
          </>
        )}
        {mode === 'success' && status === 'error' && (
          <>
            <div className="alert alert-danger" data-testid="pay-error">{err}</div>
            {back}
          </>
        )}
      </div>
    </div>
  );
}
